import { makeStyles } from "tss-react/mui"
import { useEffect, useState } from "react"
import { Box, Grid, Switch, Typography } from "@mui/material"
import { useFormikContext } from "formik"
import { CreateRoomResourceForm } from "../../../resourcesTypes"
import Cafe from "./tableSettingAssets/Cafe.png"
import Cinema from "./tableSettingAssets/Cinema.png"
import Conference from "./tableSettingAssets/Conference.png"
import Group from "./tableSettingAssets/Group.png"
import Herringbone from "./tableSettingAssets/Herringbone.png"
import MeetingRoom from "./tableSettingAssets/Meeting-room.png"
import Negotiation from "./tableSettingAssets/Negotiation.png"
import School from "./tableSettingAssets/School.png"
import UForm from "./tableSettingAssets/U-form.png"
import { LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { uniq } from "lodash"

const useStyles = makeStyles()(() => ({
    container: {
        padding: "1rem",
    },
    card: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "1rem",
        gap: "0.5rem",
    },
    image: {
        width: "8rem",
        height: "8rem",
        objectFit: "contain",
        border: "1px solid #CBD5E1",
    },
    switchContainer: {
        padding: "1rem",
        border: "1px solid #CBD5E1",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "0.5rem",
    },
}))

const seatingOptions = [
    { id: 1, label: "Meeting room", img: MeetingRoom },
    { id: 2, label: "Cinema", img: Cinema },
    { id: 3, label: "U-form", img: UForm },
    { id: 4, label: "School", img: School },
    { id: 5, label: "Group", img: Group },
    { id: 6, label: "Negotiation", img: Negotiation },
    { id: 7, label: "Cafe", img: Cafe },
    { id: 8, label: "Conference", img: Conference },
    { id: 9, label: "Herringbone", img: Herringbone },
]

export function FormTableArrangementsContent() {
    const { setFieldValue, values } = useFormikContext<CreateRoomResourceForm>()
    const { classes } = useStyles()
    const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: "standard" | "option" | null }>({})

    useEffect(() => {
        const initialSelections: { [key: string]: "standard" | "option" | null } = {}

        // Map the current tableSetups to selectedOptions, setting all to "option"
        seatingOptions.forEach(({ id, label }) => {
            const isInSetups = values.roomDetails.tableSetups?.includes(id)
            initialSelections[label] = isInSetups ? "option" : null
        })

        setSelectedOptions(initialSelections)
    }, [])

    const handleToggle = (label: string, id: number, type: "standard" | "option") => {
        setSelectedOptions((prev) => {
            const newSelections = { ...prev }
            const newSelection = prev[label] === type ? null : type

            if (type === "standard") {
                // If setting a new standard, clear the previous standard
                Object.keys(newSelections).forEach((key) => {
                    if (key !== label && newSelections[key] === "standard") {
                        newSelections[key] = null // Clear previous standard
                    }
                })
                newSelections[label] = newSelection
            } else {
                // If toggling "option", ensure it doesn't affect the standard
                newSelections[label] = newSelection
            }

            // Update tableSetups: include the standard and all options
            const standardId = seatingOptions.find((option) => newSelections[option.label] === "standard")?.id
            const optionIds = seatingOptions
                .filter((option) => newSelections[option.label] === "option")
                .map((option) => option.id)

            const updatedSetups = standardId
                ? [standardId, ...optionIds] // Include standard if it exists
                : [...optionIds]

            setFieldValue("roomDetails.tableSetups", uniq(updatedSetups))

            return newSelections
        })
    }

    return (
        <Grid container spacing="1rem" className={classes.container}>
            {seatingOptions.map(({ label, img, id }) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                    <Box className={classes.card}>
                        <Typography variant="h6" mb="1.25rem" fontWeight={600}>
                            {label}
                        </Typography>

                        <Box className={classes.switchContainer}>
                            <Box display="flex" alignItems="center">
                                <Switch
                                    checked={selectedOptions[label] === "standard"}
                                    onChange={() => handleToggle(label, id, "standard")}
                                />
                                <LocalizedStrict id="tab-table-arrangements-standard">
                                    <Typography>Standard</Typography>
                                </LocalizedStrict>
                            </Box>

                            <Box display="flex" alignItems="center">
                                <Switch
                                    checked={selectedOptions[label] === "option"}
                                    onChange={() => handleToggle(label, id, "option")}
                                />
                                <LocalizedStrict id="tab-table-arrangements-option">
                                    <Typography>Option</Typography>
                                </LocalizedStrict>
                            </Box>
                            <img src={img} alt={label} className={classes.image} />
                        </Box>
                    </Box>
                </Grid>
            ))}
        </Grid>
    )
}

